import React from "react";
import {
    BsFillHandThumbsUpFill,
    BsFillHandThumbsDownFill,
} from "react-icons/bs";
import { FaCheckCircle, FaStar } from "react-icons/fa";
import { ReactComponent as AiIcon } from "@images/ai_icon.svg";
import { Row, Col, Table, Typography } from "antd";
import WidgetCard from "./WidgetCard";
import { startCase } from "@ts/utils";
import { SatisfactionDriversSummaryWidgetProps, ISatisfactionDriversSummaryProps, ProductDeepdiveModules, CommonWidgetMetrics } from "@ts/common-types";
import { filter } from "underscore";

const SummaryTable:React.FC<ISatisfactionDriversSummaryProps> = ({
    columns,
    dataSource,
}) => {
    return (
        <Table
            className="mt-4"
            {...{
                columns,
                dataSource,
            }}
            pagination={false}
            rowKey="name"
            size="small"
            bordered={false}
            scroll={{ y:185 }}
        />
    );
};

const SatisfactionDriversSummaryWidget: React.FC<
    SatisfactionDriversSummaryWidgetProps
> = ({ moduleType, widgetData, onDriverClickHandler }) => {
    const { positives, negatives, aiSummary } = widgetData;

    const getColumns = (moduleType: string, driverType?: string) => {
        const modulesMapping: Record<string, any> = {
            [ProductDeepdiveModules.RATINGS_REVIEWS]: [
                {
                    title: "Satisfaction Driver (By NPS Score)",
                    dataIndex: CommonWidgetMetrics.NAME,
                    key: CommonWidgetMetrics.NAME,
                    width: "65%",
                    onHeaderCell: () => ({
                        style: { fontWeight: "400", fontSize: "13px" },
                    }),
                    render: (text: string) => (
                        <Typography.Link
                            onClick={() =>
                                onDriverClickHandler(driverType, text)
                            }
                            className="text-decoration-none"
                        >
                            {startCase(text)}
                        </Typography.Link>
                    ),
                },
                {
                    title: "",
                    dataIndex: CommonWidgetMetrics.SCORE,
                    key: CommonWidgetMetrics.SCORE,
                    width: "15%",
                },
                {
                    title: "",
                    dataIndex: CommonWidgetMetrics.CHANGE,
                    key: CommonWidgetMetrics.CHANGE,
                    width: "20%",

                    render: (change: number) => (
                        <span
                            className={
                                change >= 0 ? "text-success" : "text-danger"
                            }
                        >
                            {change >= 0 ? "↑" : "↓"} {change}%
                        </span>
                    ),
                },
            ],
            [ProductDeepdiveModules.PRICING]: filter(
                [
                    {
                        title: <span className="fw-normal">Product</span>,
                        dataIndex: CommonWidgetMetrics.PRODUCT_NAME,
                        type: CommonWidgetMetrics.NEGATIVE,
                    },
                    {
                        title: <span className="fw-normal">Price Drop</span>,
                        dataIndex: CommonWidgetMetrics.PRICE_DROP,
                        type: CommonWidgetMetrics.NEGATIVE,
                        render: (change: number) => <span className={`text-${change >= 0 ? "success" : "danger"}`}>{change}%</span>,
                    },
                    {
                        title: <span className="fw-normal">RPI</span>,
                        dataIndex: CommonWidgetMetrics.RPI,
                        type: CommonWidgetMetrics.NEGATIVE,
                    },
                    {
                        title: <span className="fw-normal">Product</span>,
                        dataIndex: CommonWidgetMetrics.PRODUCT_NAME,
                        type: CommonWidgetMetrics.POSTITIVE,
                    },
                    {
                        title: <span className="fw-normal">Price Drop</span>,
                        dataIndex: CommonWidgetMetrics.PRICE_DROP,
                        type: CommonWidgetMetrics.POSTITIVE,
                        render: (change: number) => <span className={`text-${change >= 0 ? "success" : "danger"}`}>{change}%</span>,
                    },
                    {
                        title: <span className="fw-normal">RPI</span>,
                        dataIndex: CommonWidgetMetrics.RPI,
                        type: CommonWidgetMetrics.POSTITIVE,
                    },
                ],
                (pd) => pd.type === driverType
            ),
            [ProductDeepdiveModules.VISIBILITY]: [
                {
                    title: <span className="fw-normal">Keyword</span>,
                    dataIndex: CommonWidgetMetrics.KEYWORD_NAME,
                    type: CommonWidgetMetrics.NEGATIVE,
                },
                {
                    title: <span className="fw-normal"></span>,
                    dataIndex: CommonWidgetMetrics.SV_CHANGE,
                    type: CommonWidgetMetrics.NEGATIVE,
                    render: (change: number) => <span className={`text-${change >= 0 ? "success" : "danger"}`}>{change}%</span>,
                },
            ],
            [ProductDeepdiveModules.AVAILABILITY]: [
                {
                    title: <span className="fw-normal"></span>,
                    dataIndex: CommonWidgetMetrics.NAME,
                    type: CommonWidgetMetrics.NEGATIVE,
                },
                {
                    title: <span className="fw-normal"></span>,
                    dataIndex: CommonWidgetMetrics.CHANGE,
                    type: CommonWidgetMetrics.NEGATIVE,
                    render: (change: number) => <span className={`text-${change >= 0 ? "success" : "danger"}`}>{change}%</span>,
                },
            ]
        };
        return modulesMapping[moduleType];
    };

    return (
        <Row gutter={16} className="my-5 py-3">
            <Col xs={24} sm={12} lg={8}>
                <WidgetCard className="h-100">
                    <h5 className="text-success d-flex align-items-center">
                        <BsFillHandThumbsUpFill className="me-2" />
                        What's Working
                    </h5>
                    <SummaryTable
                        columns={getColumns(moduleType,CommonWidgetMetrics.POSTITIVE)}
                        dataSource={positives}
                    />
                </WidgetCard>
            </Col>

            <Col xs={24} sm={12} lg={8}>
                <WidgetCard className="h-100">
                    <h5 className="text-danger d-flex align-items-center">
                        <BsFillHandThumbsDownFill className="me-2" />
                        What's Not Working
                    </h5>
                    <SummaryTable
                        columns={getColumns(moduleType,CommonWidgetMetrics.NEGATIVE)}
                        dataSource={negatives}
                    />
                </WidgetCard>
            </Col>

            <Col xs={24} sm={12} lg={8}>
                <WidgetCard className="h-100 ai-card">
                    <h5 className="mb-3 d-flex align-items-center">
                        <AiIcon
                            style={{ height: "20px", marginRight: "8px" }}
                        />
                        <span className="ai-tab-text fw-bold">AI Summary</span>
                    </h5>
                    <div
                        className={
                            aiSummary
                                ? "small"
                                : "d-flex pt-5 fs-6 align-items-center justify-content-center text-secondary"
                        }
                    >
                        {aiSummary ? (
                            <>
                                {aiSummary?.review && (
                                    <p className="d-flex align-items-center">
                                        <FaStar className="me-2" />
                                        {aiSummary.review}
                                    </p>
                                )}
                                { moduleType !== ProductDeepdiveModules.PRICING && aiSummary?.rating && (
                                    <p className="d-flex align-items-center">
                                        <FaCheckCircle className="me-2" />
                                        {aiSummary.rating}
                                    </p>
                                )}
                                { moduleType !== ProductDeepdiveModules.PRICING && aiSummary?.ranking && (
                                    <p className="d-flex align-items-center">
                                        <BsFillHandThumbsUpFill className="me-2" />
                                    </p>
                                )}
                            </>
                        ) : (
                            "No Data"
                        )}
                    </div>
                </WidgetCard>
            </Col>
        </Row>
    );
};

export default SatisfactionDriversSummaryWidget;